/**
 * Promo Code URL Parameter Handler
 * Automatically applies promo codes from URL query parameters to the cart
 * Integrates with SlideCartHQ (AMP cart) and displays toast notifications
 * Uses custom API for validation and Shopify's discount endpoint for application
 */
(function() {
  'use strict';

  // Track if promo code has been processed to prevent duplicate applications
  let promoCodeProcessed = false;

  // Enable debug logging (set to false in production)
  const DEBUG = false;

  /**
   * Debug logging function
   * @param {string} message - Log message
   * @param {*} data - Optional data to log
   * @private
   */
  function log(message, data) {
    if (DEBUG) {
      console.log('[PromoCodeHandler]', message, data || '');
    }
  }

  /**
   * Get promo code from URL parameters
   * Supports 'promo', 'promo_code', and 'promo-code' parameters
   * @returns {string|null} - Promo code value or null if not found
   * @private
   */
  function getPromoCodeFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    // Check all three parameter variations: 'promo', 'promo_code', and 'promo-code'
    const promoCode = urlParams.get('promo') || urlParams.get('promo_code') || urlParams.get('promo-code');
    log('Checking URL parameters:', {
      promo: urlParams.get('promo'),
      promo_code: urlParams.get('promo_code'),
      'promo-code': urlParams.get('promo-code'),
      found: promoCode
    });
    return promoCode;
  }

  /**
   * Remove promo code parameters from URL without page reload
   * Removes 'promo', 'promo_code', and 'promo-code' parameters
   * @private
   */
  function removePromoParametersFromURL() {
    const url = new URL(window.location.href);
    const hadPromo = url.searchParams.has('promo');
    const hadPromoCode = url.searchParams.has('promo_code');
    const hadPromoCodeHyphen = url.searchParams.has('promo-code');
    url.searchParams.delete('promo');
    url.searchParams.delete('promo_code');
    url.searchParams.delete('promo-code');
    window.history.replaceState({}, '', url);
    log('Removed promo parameters from URL', { hadPromo, hadPromoCode, hadPromoCodeHyphen });
  }

  /**
   * Update coupon input field to show the applied discount code
   * @param {string} discountCode - The discount code to display
   * @private
   */
  function updateCouponInputField(discountCode) {
    const couponInput = document.getElementById('cartCouponInput');
    const couponError = document.getElementById('cartCouponError');
    
    if (couponInput) {
      couponInput.value = discountCode;
      couponInput.style.color = '#4CAF50'; // Green color to indicate it's applied
      log('Updated coupon input field with code:', discountCode);
    }
    
    if (couponError) {
      couponError.textContent = `Discount code "${discountCode}" has been applied`;
      couponError.style.display = 'block';
      couponError.style.color = '#4CAF50'; // Green for success
      log('Updated coupon error/success message');
    }
  }

  /**
   * Refresh SlideCartHQ cart UI
   * Uses multiple methods to ensure compatibility
   * @private
   */
  function refreshSlideCartHQ() {
    log('Refreshing SlideCartHQ...');
    
    // Method 1: Use SlideCartHQ refresh method if available
    if (window.SlideCartHQ && typeof window.SlideCartHQ.refresh === 'function') {
      log('Using SlideCartHQ.refresh()');
      window.SlideCartHQ.refresh();
    } else {
      log('SlideCartHQ.refresh() not available');
    }

    // Method 2: Use SLIDECART_UPDATE callback if available
    if (typeof window.SLIDECART_UPDATE === 'function') {
      log('Using SLIDECART_UPDATE()');
      window.SLIDECART_UPDATE(function() {
        log('SlideCartHQ updated via SLIDECART_UPDATE');
      });
    } else {
      log('SLIDECART_UPDATE() not available');
    }

    // Method 3: Dispatch cart refresh events (used by cart-oatmilk-handler.js)
    log('Dispatching cart refresh events');
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
      detail: {},
      bubbles: true
    }));

    document.dispatchEvent(new CustomEvent('cart:updated', {
      detail: {},
      bubbles: true
    }));
  }

  /**
   * Show toast notification
   * @param {string} type - Toast type (success, error, warning, info)
   * @param {string} title - Toast title
   * @param {string} message - Toast message
   * @private
   */
  function showToast(type, title, message) {
    log(`Attempting to show toast: ${type} - ${title}`, message);
    
    const toastOptions = {
      type: type,
      title: title,
      message: message,
      duration: 5000,
      from: 'top-right'
    };

    // Check if Toast is available
    if (typeof window.Toast !== 'undefined' && typeof window.Toast.fire === 'function') {
      log('Toast available, showing notification');
      window.Toast.fire(toastOptions);
      return;
    }

    // Wait for Toast to be available (since scripts are deferred)
    log('Toast not yet available, waiting...');
    let attempts = 0;
    const maxAttempts = 50; // 5 seconds max wait
    
    const checkToast = setInterval(() => {
      attempts++;
      
      if (typeof window.Toast !== 'undefined' && typeof window.Toast.fire === 'function') {
        log(`Toast available after ${attempts * 100}ms, showing notification`);
        clearInterval(checkToast);
        window.Toast.fire(toastOptions);
      } else if (attempts >= maxAttempts) {
        log('Toast not available after max wait time, using console fallback');
        clearInterval(checkToast);
        console.log(`[PromoCodeHandler] ${title}: ${message}`);
        // Also try to show browser alert as last resort
        if (type === 'error' || type === 'success') {
          alert(`${title}: ${message}`);
        }
      }
    }, 100);
  }

  /**
   * Apply promo code to cart using API validation and Shopify discount endpoint
   * @param {string} promoCode - The promo code to apply
   * @returns {Promise<boolean>} - Success status
   * @private
   */
  async function applyPromoCode(promoCode) {
    log('Applying promo code:', promoCode);
    
    try {
      // Check if API endpoint is available
      if (!window.apiEndpoint) {
        log('API endpoint not available, cannot validate promo code');
        showToast('error', 'Promo Code Error', 'API endpoint not configured. Please contact support.');
        return false;
      }

      // Step 1: Validate promo code via custom API
      log('Validating promo code via API...');
      const res = await fetch(`${window.apiEndpoint}/promo/details`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ promoCode }),
      });

      // Parse JSON response regardless of status code
      const json = await res.json();
      log('API response:', json);

      // Check if the response contains an error (works for both 404 and 200 responses)
      if (json.error) {
        log('API returned error:', json.error);
        showToast('error', 'Promo Code Error', json.error);
        return false;
      }

      // If response is not ok and no error field, show generic error
      if (!res.ok) {
        log('API request failed:', res.status);
        showToast('error', 'An error occurred.', 'Failed to get promo details. Contact support.');
        return false;
      }

      // Step 2: Check if promo is active
      if (json?.promo) {
        const { promo } = json;
        
        if (!promo.isActive) {
          log('Promo code is not active');
          showToast('error', 'Promo Code Error', 'This promo code is no longer active.');
          return false;
        }

        // Step 3: Apply the discount to the cart using Shopify's discount endpoint
        log('Applying discount via Shopify endpoint...');
        const discountResponse = await fetch(`/discount/${promoCode}`, {
          method: 'HEAD',
          redirect: 'manual' // Don't follow redirects, just check the response
        });

        log('Discount endpoint response status:', discountResponse.status);

        // Update coupon input field to show the applied code
        updateCouponInputField(promoCode);

        // Refresh SlideCartHQ with a delay to ensure cart update is complete
        setTimeout(() => {
          refreshSlideCartHQ();
        }, 500);

        // Show success message with promo details
        log('Promo code applied successfully');
        showToast('success', `${promo.promoCode} promo code applied!`, promo.description || 'Discount has been applied to your cart.');

        return true;
      } else {
        log('Promo code not found in API response');
        showToast('error', 'Promo Code Error', 'This promo code is not valid.');
        return false;
      }

    } catch (error) {
      log('Error applying promo code:', error);
      console.error('[PromoCodeHandler] Error applying promo code:', error);
      showToast('error', 'Promo Code Error', 'Failed to apply promo code. Contact support.');
      return false;
    }
  }

  /**
   * Process promo code from URL parameter
   * @private
   */
  async function processPromoCode() {
    log('processPromoCode called');
    
    // Prevent duplicate processing
    if (promoCodeProcessed) {
      log('Promo code already processed, skipping');
      return;
    }

    // Get promo code from URL (supports 'promo', 'promo_code', and 'promo-code')
    const promoCode = getPromoCodeFromURL();

    // If no promo parameter, exit early
    if (!promoCode) {
      log('No promo code found in URL');
      return;
    }

    log('Promo code found:', promoCode);

    // Mark as processed immediately to prevent duplicate runs
    promoCodeProcessed = true;

    const trimmedCode = promoCode.trim();
    log('Trimmed promo code:', trimmedCode);

    // Remove promo parameter from URL immediately (before processing)
    removePromoParametersFromURL();

    // Show loading toast
    log('Showing loading toast');
    showToast('info', 'Applying Promo Code', 'Please wait...');

    // Apply the promo code
    const success = await applyPromoCode(trimmedCode);
    log('Promo code application result:', success);

    // Reset processed flag on error so user can try again if needed
    if (!success) {
      promoCodeProcessed = false;
    }
  }

  /**
   * Initialize promo code handler
   * Waits for DOM to be ready before processing
   * @private
   */
  function init() {
    log('Initializing promo code handler');
    log('Document ready state:', document.readyState);
    log('Current URL:', window.location.href);
    log('Toast available:', typeof window.Toast !== 'undefined');
    log('API endpoint available:', typeof window.apiEndpoint !== 'undefined' && window.apiEndpoint);
    
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      log('Waiting for DOMContentLoaded');
      document.addEventListener('DOMContentLoaded', () => {
        log('DOMContentLoaded fired');
        setTimeout(processPromoCode, 200); // Give scripts more time to load
      });
    } else {
      // DOM is already ready, but wait a bit for other scripts to initialize
      log('DOM already ready, scheduling processPromoCode');
      setTimeout(processPromoCode, 300); // Increased delay to ensure Toast is loaded
    }
  }

  // Initialize on page load
  log('Script loaded, calling init()');
  init();
})();
